import { GoogleGenerativeAI } from '@google/generative-ai';

class GeminiService {
  constructor() {
    this.genAI = null;
    this.model = null;
    this.apiKey = null;
    this.selectedModel = 'gemini-2.0-flash';
  }

  setApiKey(key) {
    this.apiKey = key;
    if (key) {
      try {
        this.genAI = new GoogleGenerativeAI(key);
        this.model = this.genAI.getGenerativeModel({ model: this.selectedModel });
        return true;
      } catch (error) {
        console.error('Error initializing Gemini:', error);
        return false;
      }
    } else {
      this.genAI = null;
      this.model = null;
      return false;
    }
  }

  setModel(modelName) {
    this.selectedModel = modelName;
    // Re-initialize the model if we have an API key
    if (this.apiKey) {
      try {
        this.model = this.genAI.getGenerativeModel({ model: this.selectedModel });
        return true;
      } catch (error) {
        console.error('Error updating Gemini model:', error);
        return false;
      }
    }
    return false;
  }

  getSelectedModel() {
    return this.selectedModel;
  }

  async generateResponse(message, conversationHistory = []) {
    if (!this.model) {
      throw new Error('Gemini API key not set');
    }

    try {
      const chat = this.model.startChat({
        history: conversationHistory.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'model',
          parts: [{ text: msg.text }]
        }))
      });

      const result = await chat.sendMessage(message);
      const response = result.response;
      return response.text();
    } catch (error) {
      console.error('Error generating Gemini response:', error);
      throw error;
    }
  }

  async generateStreamResponse(message, conversationHistory = [], onChunk) {
    if (!this.model) {
      throw new Error('Gemini API key not set');
    }

    try {
      const chat = this.model.startChat({
        history: conversationHistory.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'model',
          parts: [{ text: msg.text }]
        }))
      });

      const result = await chat.sendMessageStream(message);
      let text = '';
      
      for await (const chunk of result.stream) {
        const chunkText = chunk.text();
        text += chunkText;
        if (onChunk) {
          onChunk(chunkText, text);
        }
      }
      
      return text;
    } catch (error) {
      console.error('Error generating Gemini stream response:', error);
      throw error;
    }
  }

  async generateBusinessResponse(userMessage, businessContext = {}) {
    if (!this.model) {
      throw new Error('Gemini API key not set');
    }

    const businessName = businessContext.businessName || 'ABC Services';
    const projectDescription = businessContext.projectDescription || '';

    // Create a user message that includes the system instruction inline
    const fullUserMessage = `You are a customer service representative for ${businessName}. The caller is an AI assistant calling on behalf of a client. 

Context: ${projectDescription}

Respond professionally as ${businessName} staff. Include specific pricing, availability, and next steps. Keep responses 1-2 sentences.

User message: ${userMessage}

Your response as ${businessName}:`;

    try {
      const result = await this.model.generateContent(fullUserMessage);
      const response = result.response;
      return response.text();
    } catch (error) {
      console.error('Error generating business response:', error);
      throw error;
    }
  }

  isConfigured() {
    return this.model !== null;
  }

  getApiKey() {
    return this.apiKey;
  }
}

const geminiService = new GeminiService();
export default geminiService;